import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const topicOptions = [
  "General",
  "Sponsorship",
  "Vendor",
  "VIP & Groups",
  "Press/Media",
  "Other",
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "",
    message: "",
  });
  const [topicDropdownOpen, setTopicDropdownOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.topic || !formData.message) return;
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          subject: formData.topic,
          message: formData.message,
          inquiry_type: formData.topic.toLowerCase().replace(/[^a-z]/g, '-')
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20">
        <motion.div 
          className="text-center max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C24E2C] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#D4AF37]/20">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase mb-3" style={{ color: "#F4F1E8" }}>
            MESSAGE <span className="text-terracotta">SENT</span>
          </h2>
          <div className="w-16 h-0.5 mx-auto my-4" style={{ background: "#D4AF37" }} />
          <p className="text-white/50 text-lg mb-8">
            Our team will reach out soon.
          </p>
          <Link to="/">
            <Button className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-8 py-4 font-accent font-bold">
              BACK TO HOME
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      {/* Back Navigation */}
      <Link 
        to="/" 
        className="fixed top-24 left-6 z-40 flex items-center gap-2 text-white/50 hover:text-white transition-colors"
        data-testid="back-to-home"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="font-accent text-sm uppercase tracking-wider">Back</span>
      </Link>

      {/* Hero Section */}
      <section 
        className="pt-32 md:pt-40 pb-16 px-4 relative overflow-hidden"
        data-testid="contact-hero"
      >
        {/* Emerald vignette */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(15,59,46,0.12) 0%, transparent 70%)',
          }}
        />

        <motion.div 
          className="text-center max-w-3xl mx-auto relative z-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight mb-6" style={{ color: "#F4F1E8" }}>
            CONTACT <span className="text-terracotta">US</span>
          </h1>
          
          {/* Gold divider */}
          <div className="w-20 h-0.5 mx-auto my-6" style={{ background: "#D4AF37" }} />
          
          <p className="text-white/50 text-lg md:text-xl font-light">
            Questions, partnerships, vendors, or VIP requests — reach out.
          </p>
        </motion.div>
      </section>

      {/* Contact Form Section */}
      <section 
        className="py-12 md:py-20 px-4 relative"
        style={{ background: "#0A0A0A" }}
        data-testid="contact-form-section"
      >
        {/* Top gold divider */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px" style={{ background: "#D4AF37" }} />
        
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(194,78,44,0.04) 0%, transparent 60%)',
          }}
        />

        <div className="max-w-lg mx-auto relative z-10">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <Input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              required
              data-testid="contact-name"
            />

            {/* Email */}
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              required
              data-testid="contact-email"
            />

            {/* Phone (optional) */}
            <Input
              type="tel"
              placeholder="Phone (optional)"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              data-testid="contact-phone"
            />

            {/* Topic Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setTopicDropdownOpen(!topicDropdownOpen)}
                className="w-full py-5 px-5 rounded-lg text-left flex items-center justify-between transition-all duration-300"
                style={{
                  background: '#111111',
                  border: formData.topic ? '1px solid #D4AF37' : '1px solid rgba(255,255,255,0.1)',
                }}
                data-testid="contact-topic-dropdown"
              >
                <span className={formData.topic ? 'text-[#F4F1E8]' : 'text-white/30'}>
                  {formData.topic || 'Select a topic...'}
                </span>
                <svg className={`w-5 h-5 text-white/40 transition-transform ${topicDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {topicDropdownOpen && (
                <motion.div 
                  className="absolute top-full left-0 right-0 mt-2 rounded-lg overflow-hidden z-20"
                  style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {topicOptions.map((topic) => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => {
                        setFormData({...formData, topic});
                        setTopicDropdownOpen(false);
                      }}
                      className="w-full py-3 px-5 text-left hover:bg-[#1A1A1A] transition-colors text-[#F4F1E8]"
                      data-testid={`topic-${topic.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      {topic}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Message */}
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Your message..."
              className="w-full h-36 md:h-44 bg-[#111111] border border-white/10 text-white placeholder:text-white/30 py-4 px-5 rounded-lg text-base resize-none focus:border-[#D4AF37] focus:outline-none transition-colors"
              required
              data-testid="contact-message"
            />

            {/* Submit Button */}
            <div className="pt-4">
              {error && (
                <p className="text-terracotta text-sm text-center mb-4" data-testid="contact-error">{error}</p>
              )}
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] py-6 font-accent font-bold text-base tracking-wider rounded-lg transition-all hover:-translate-y-1 disabled:opacity-50"
                data-testid="contact-submit"
              >
                {loading ? "SENDING..." : "SEND MESSAGE"}
              </Button>
            </div>
          </form>
        </div>

        {/* Bottom gold divider */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px" style={{ background: "#D4AF37" }} />
      </section>

      {/* Footer */}
      <footer 
        className="py-10 px-4 border-t border-white/5"
        style={{ background: "#050505" }}
        data-testid="contact-footer"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h4 className="font-heading font-bold text-lg mb-1" style={{ color: "#F4F1E8" }}>
            CINCO <span className="text-terracotta">DE</span> CRAWL
          </h4>
          <p className="text-white/30 text-xs mb-4">The Ultimate Bar Crawl Tour</p>
          <div className="w-8 h-0.5 mx-auto mb-4" style={{ background: "#1F4E3D" }} />
          <p className="text-white/20 text-xs">© 2026 Cinco De Crawl. Must be 21+.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
